/* eslint-disable no-console */
function userSessionExpiredPromise(): Promise<boolean> {
	return new Promise((resolve) => {
		const timeout = setTimeout(
			() => {
				resolve(false);
			},
			1000 * 60 * 10,
		);

		globalThis.af?.userSession?.expired?.(() => {
			clearTimeout(timeout);
			resolve(true);
		});
	});
}

type ResponseError = {
	handled: boolean;
	canRetry: boolean;
	error: string | Record<string, any>;
	message: string;
	stack?: string;
	stackTrace?: string;
	status?: number;
	statusText?: string;
};

function isXHR(response: Response | XMLHttpRequest): response is XMLHttpRequest {
	return globalThis.XMLHttpRequest !== undefined && response instanceof XMLHttpRequest;
}

export async function handleResponseError(response: Response | XMLHttpRequest): Promise<ResponseError> {
	const contentType = isXHR(response)
		? response.getResponseHeader("Content-Type")
		: response.headers.get("Content-Type") || "";
	let { status, statusText } = response;
	let data: any;

	if (isXHR(response)) {
		data = contentType?.includes("json") ? JSON.parse(response.response) : response.response;
	} else {
		data = contentType?.includes("json") ? await response.json() : await response.text();
	}

	if (status === 401 && data === "Session expired") {
		return {
			handled: true,
			canRetry: await userSessionExpiredPromise(),
			error: data,
			message: data,
			status,
			statusText,
		};
	}

	if (typeof data === "object" && Object.hasOwn(data, "error")) {
		let message = typeof data.error === "object" ? data.error.message : data.error;
		let stack = typeof data.error === "object" ? data.error.stackTrace : data.stack;
		const error: ResponseError = {
			...data,
			handled: false,
			canRetry: false,
			message,
			stackTrace: stack,
			stack,
		};

		if (Object.hasOwn(data, "stack") && process.env.NODE_ENV !== "test") {
			console.groupCollapsed(data.error);
			console.log(data.stack);
			console.groupEnd();
		}

		return error;
	}

	let contentMaybeHtml = contentType?.includes("text/html");
	if (!contentMaybeHtml && data[0] == "<") {
		contentMaybeHtml = true;
	}

	if (data && data.length > 0 && contentMaybeHtml) {
		let errorRegEx = /<pre>((.|\n)*?)<\/pre>/;
		let regExArray = errorRegEx.exec(data);
		let error = `Sorry, but there seems to be an error.\nThe server responded with status code: ${status}`;
		if (regExArray) {
			error = regExArray[1];
		}
		return {
			handled: false,
			canRetry: false,
			error,
			message: error,
			status,
			statusText,
		};
	}

	let message: string = !data && status ? `${status} ${statusText}` : data || "Unspecified error";

	return {
		handled: false,
		canRetry: false,
		error: message,
		message,
		status,
		statusText,
	};
}
